// src/hooks/useWealthData.ts
import { useState, useEffect, useCallback, useRef } from 'react';
import { capacitorDbService } from '../services/capacitorDb.service';
import { wealthSync } from '../services/wealthSync.service';

interface AssetItem {
  id: string;
  name: string;
  type: string;
  value: number;
  currentValue: number;
}

interface LiabilityItem {
  id: string;
  name: string;
  type: string;
  originalAmount: number;
  remainingAmount: number;
  monthlyPayment: number;
  interestRate: number;
}

export interface WealthData {
  assets: AssetItem[];
  liabilities: LiabilityItem[];
  totalAssets: number;
  totalLiabilities: number;
  netWorth: number;
  totalMonthlyDebt: number;
  assetCategories: Record<string, { total: number; count: number }>;
  liabilityCategories: Record<string, { total: number; count: number }>;
  lastUpdated: Date | null;
  isLoading: boolean;
  error: string | null;
}

const DEFAULT_DATA: WealthData = {
  assets: [],
  liabilities: [],
  totalAssets: 0,
  totalLiabilities: 0,
  netWorth: 0,
  totalMonthlyDebt: 0,
  assetCategories: {},
  liabilityCategories: {},
  lastUpdated: null,
  isLoading: false,
  error: null,
};

// ============================================================
// GLOBAL CACHE — module-level singleton
// ============================================================
let globalCache: WealthData | null = null;
let lastFetchTime = 0;
const CACHE_TTL = 2000; // 2 seconds
let cachedUserId: string | null = null;

// ============================================================
// PERSISTENT GLOBAL SUBSCRIPTION — created once, never destroyed
// Components register/unregister callbacks instead of creating
// their own subscriptions.
// ============================================================
const globalRefreshCallbacks = new Set<() => void>();
const globalUnsubscribe = wealthSync.subscribe(() => {
  console.log('[useWealthData] Global wealthSync notifying', globalRefreshCallbacks.size, 'callbacks');
  globalRefreshCallbacks.forEach(cb => {
    try { cb(); } catch (e) { console.error('[useWealthData] Callback error:', e); }
  });
});

export const useWealthData = (autoRefresh: boolean = true) => {
  const [data, setData] = useState<WealthData>(globalCache || DEFAULT_DATA);
  const isMounted = useRef(true);
  const refreshInProgress = useRef(false);
  const userIdRef = useRef<string | null>(null);

  // ============================================================
  // STABLE getCurrentUserId — no dependency churn
  // NOTE: __AUTH_STORE__ is not set anywhere in this codebase,
  // so we rely on __NURWALLET_USER_ID__ or DB fallback.
  // ============================================================
  const getCurrentUserId = useCallback(async (): Promise<string | null> => {
    const globalId = (window as any).__NURWALLET_USER_ID__;
    if (globalId) return globalId;
    try {
      await capacitorDbService.init();
      const guestUser = await capacitorDbService.getUserByEmail('guest@nurwallet.local');
      if (guestUser) {
        (window as any).__NURWALLET_USER_ID__ = guestUser.id;
        return guestUser.id;
      }
    } catch (e) {}
    try {
      const allUsers = await capacitorDbService.getAllUsers?.();
      if (allUsers && allUsers.length > 0) {
        (window as any).__NURWALLET_USER_ID__ = allUsers[0].id;
        return allUsers[0].id;
      }
    } catch (e) {}
    return null;
  }, []);

  // ============================================================
  // STABLE calculateWealthData — pure function, memoized
  // ============================================================
  const calculateWealthData = useCallback((assets: AssetItem[], liabilities: LiabilityItem[]): WealthData => {
    const totalAssets = assets.reduce((sum, a) => sum + (a.currentValue || a.value || 0), 0);
    const totalLiabilities = liabilities.reduce((sum, l) => sum + (l.remainingAmount || 0), 0);
    const totalMonthlyDebt = liabilities.reduce((sum, l) => sum + (l.monthlyPayment || 0), 0);

    const assetCategories: Record<string, { total: number; count: number }> = {};
    assets.forEach(a => {
      if (!assetCategories[a.type]) assetCategories[a.type] = { total: 0, count: 0 };
      assetCategories[a.type].total += (a.currentValue || a.value || 0);
      assetCategories[a.type].count += 1;
    });

    const liabilityCategories: Record<string, { total: number; count: number }> = {};
    liabilities.forEach(l => {
      if (!liabilityCategories[l.type]) liabilityCategories[l.type] = { total: 0, count: 0 };
      liabilityCategories[l.type].total += (l.remainingAmount || 0);
      liabilityCategories[l.type].count += 1;
    });

    return {
      assets, liabilities, totalAssets, totalLiabilities,
      netWorth: totalAssets - totalLiabilities, totalMonthlyDebt,
      assetCategories, liabilityCategories,
      lastUpdated: new Date(), isLoading: false, error: null,
    };
  }, []);

  // ============================================================
  // STABLE refresh — prevents concurrent calls, respects cache
  // ============================================================
  const refresh = useCallback(async (force: boolean = false) => {
    if (refreshInProgress.current) {
      console.log('[useWealthData] Refresh already in progress, skipping');
      return globalCache || DEFAULT_DATA;
    }

    const now = Date.now();
    const currentUserId = userIdRef.current;
    const cacheValid = !force && globalCache && (now - lastFetchTime) < CACHE_TTL && cachedUserId === currentUserId;

    if (cacheValid) {
      if (isMounted.current) setData(globalCache!);
      return globalCache!;
    }

    refreshInProgress.current = true;
    if (isMounted.current) setData(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const userId = await getCurrentUserId();

      if (cachedUserId !== userId) {
        globalCache = null;
        lastFetchTime = 0;
      }

      if (!userId) {
        const emptyData = calculateWealthData([], []);
        globalCache = emptyData;
        lastFetchTime = now;
        cachedUserId = null;
        if (isMounted.current) setData(emptyData);
        return emptyData;
      }

      const [assets, liabilities] = await Promise.all([
        capacitorDbService.getAssets(userId).catch(err => {
          console.error('[useWealthData] getAssets error:', err);
          return [];
        }),
        capacitorDbService.getLiabilities(userId).catch(err => {
          console.error('[useWealthData] getLiabilities error:', err);
          return [];
        }),
      ]);

      const computed = calculateWealthData(assets, liabilities);
      globalCache = computed;
      lastFetchTime = now;
      cachedUserId = userId;

      if (isMounted.current) setData(computed);
      return computed;
    } catch (error: any) {
      console.error('[useWealthData] Refresh error:', error);
      const errorData = { ...DEFAULT_DATA, isLoading: false, error: error.message };
      if (isMounted.current) setData(errorData);
      return errorData;
    } finally {
      refreshInProgress.current = false;
    }
  }, [getCurrentUserId, calculateWealthData]);

  // ============================================================
  // MOUNT / UNMOUNT — only set isMounted flag
  // ============================================================
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // ============================================================
  // AUTO-REFRESH on mount (once only)
  // ============================================================
  useEffect(() => {
    if (autoRefresh) {
      refresh();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================================
  // WEALTH SYNC — register callback in global Set
  // No create/destroy loop because the global subscription lives
  // forever. We only add/remove this component's callback.
  // ============================================================
  useEffect(() => {
    if (!autoRefresh) return;
    const cb = () => { refresh(true); };
    globalRefreshCallbacks.add(cb);
    console.log('[useWealthData] Registered callback. Total:', globalRefreshCallbacks.size);
    return () => {
      globalRefreshCallbacks.delete(cb);
      console.log('[useWealthData] Unregistered callback. Total:', globalRefreshCallbacks.size);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh]);

  // ============================================================
  // LEGACY EVENT LISTENER — for backward compatibility
  // ============================================================
  useEffect(() => {
    const handleSync = () => {
      console.log('[useWealthData] Legacy sync event received');
      refresh(true);
    };
    window.addEventListener('nurwallet-wealth-sync', handleSync);
    return () => window.removeEventListener('nurwallet-wealth-sync', handleSync);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { ...data, refresh, getCurrentUserId };
};

export const triggerWealthSync = () => {
  console.log('[triggerWealthSync] Called');
  wealthSync.notify();
  window.dispatchEvent(new CustomEvent('nurwallet-wealth-sync'));
};

export default useWealthData;
